/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  clearTokens,
  getCurrentUser,
  getRefreshToken,
  getToken,
  getTokenExpiresAt,
  onAuthChange,
  setTokens,
  getRememberPreference,
  getTermsAccepted,
  setTermsAccepted as setTermsAcceptedStorage
} from '../lib/auth';
import { postJson, logout as apiLogout, refreshAccessToken, scheduleProactiveRefresh, acceptTermsRequest } from '../lib/api';
import type { OrgOption, TokenResponse, UserIdentity } from '../lib/types';

interface AuthContextValue {
  user: UserIdentity | null;
  isAuthenticated: boolean;
  /** true while checking if an expired token can be refreshed on mount */
  initializing: boolean;
  /** Available orgs from the last pre-auth response (empty if single-org or not yet logged in) */
  availableOrgs: OrgOption[];
  switchOrg: (preAuthToken: string, orgId: number) => Promise<void>;
  logout: () => void;
  setAvailableOrgs: (orgs: OrgOption[]) => void;
  setPreAuthToken: (token: string | null) => void;
  preAuthToken: string | null;
  termsAccepted: boolean;
  acceptTerms: (documentVersionIds: number[]) => Promise<void>;
  setTermsAccepted: (accepted: boolean) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<UserIdentity | null>(() => getCurrentUser());
  const [availableOrgs, setAvailableOrgs] = useState<OrgOption[]>([]);
  const [preAuthToken, setPreAuthToken] = useState<string | null>(null);
  const [initializing, setInitializing] = useState(true);
  const [termsAccepted, setTermsAcceptedState] = useState<boolean>(() => getTermsAccepted());

  useEffect(() => {
    let cancelled = false;

    async function init() {
      const validToken = getToken();
      if (validToken) {
        // Token still valid — schedule proactive refresh
        const expiresAt = getTokenExpiresAt();
        if (expiresAt) scheduleProactiveRefresh(expiresAt);
      } else if (getRefreshToken()) {
        // Access token expired but refresh token exists — try to renew
        try {
          const newToken = await refreshAccessToken();
          if (!newToken && !cancelled) {
            clearTokens();
          }
        } catch {
          // Transient error — don't clear tokens, user can retry
        }
      }
      if (!cancelled) {
        setUser(getCurrentUser());
        setInitializing(false);
      }
    }

    void init();
    const unsubscribe = onAuthChange(() => {
      setUser(getCurrentUser());
      setTermsAcceptedState(getTermsAccepted());
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const switchOrg = useCallback(
    async (token: string, orgId: number) => {
      const result = await postJson<TokenResponse, { preAuthToken: string; orgId: number }>(
        '/auth/select-org',
        { preAuthToken: token, orgId },
        { auth: false, retryOnAuthError: false }
      );
      setTokens(result.accessToken, result.refreshToken, getRememberPreference());
      setTermsAcceptedStorage(result.termsAccepted);
      scheduleProactiveRefresh(result.expiresAt);
      queryClient.clear();
    },
    [queryClient]
  );

  const logout = useCallback(() => {
    void apiLogout();
    setAvailableOrgs([]);
    setPreAuthToken(null);
    queryClient.clear();
  }, [queryClient]);

  const acceptTerms = useCallback(
    async (documentVersionIds: number[]) => {
      // Cycle 9 v7.35.0: api.ts:acceptTermsRequest deixou de persistir
      // storage (era duplo). AuthContext agora e fonte unica:
      // storage + state React num so callback, atomico.
      await acceptTermsRequest(documentVersionIds);
      setTermsAcceptedStorage(true);
      setTermsAcceptedState(true);
    },
    []
  );

  const handleSetTermsAccepted = useCallback((accepted: boolean) => {
    setTermsAcceptedStorage(accepted);
    setTermsAcceptedState(accepted);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: user !== null,
      initializing,
      availableOrgs,
      switchOrg,
      logout,
      setAvailableOrgs,
      setPreAuthToken,
      preAuthToken,
      termsAccepted,
      acceptTerms,
      setTermsAccepted: handleSetTermsAccepted,
    }),
    [user, initializing, availableOrgs, switchOrg, logout, preAuthToken, termsAccepted, acceptTerms, handleSetTermsAccepted]
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
